import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadFileToCos } from '@/infrastructure/cos-file';
import { formatByteAdaptive } from '@/infrastructure/utils/digit';
import { aiAgentServer } from '@/service/agent';
import styles from './copilot-input.module.scss';
import AgentDelete from './icons/agent-delete.react.svg?react';
import FileIcon from './icons/file-icon.svg?react';

// 扩展的文件接口，包含上传状态
export interface ExtendedFile {
  id: string; // UUID
  file: File; // 原始文件
  uploadStatus: 'pending' | 'uploading' | 'success' | 'error';
  uploadProgress?: number; // 上传进度 0-100
  uploadUrl?: string; // 上传成功后的URL
  errorMessage?: string; // 错误信息
}

interface FileItemProps {
  extendedFile: ExtendedFile;
  onDelete: (id: string) => void;
  onStatusChange?: (id: string, status: Partial<ExtendedFile>) => void;
}

export const FileItem: React.FC<FileItemProps> = ({
  extendedFile,
  onDelete,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const { file, uploadStatus } = extendedFile;
  const [localStatus, setLocalStatus] = useState(extendedFile);

  // 更新本地状态并通知父组件
  const updateStatus = (updates: Partial<ExtendedFile>) => {
    const newStatus = { ...localStatus, ...updates };
    setLocalStatus(newStatus);
    onStatusChange?.(extendedFile.id, updates);
  };

  // 文件上传函数
  const uploadFile = async () => {
    try {
      // 更新状态为上传中
      updateStatus({ uploadStatus: 'uploading', uploadProgress: 0 });

      // 获取上传URL
      const uploadResponse = await aiAgentServer.getUploadURL({
        fileName: file.name,
        uploadType: 1,
      });

      if (uploadResponse?.baseRsp?.code !== 0) {
        throw new Error(t('获取上传URL失败'));
      }

      // 上传到COS
      const cosResult = await uploadFileToCos({
        file,
        secret: {
          secretId: uploadResponse.cosSecretInfo?.secretId,
          secretKey: uploadResponse.cosSecretInfo?.secretKey,
          sessionToken: uploadResponse.cosSecretInfo?.token,
          expiredTime: uploadResponse.cosSecretInfo?.expiredTime,
          region: uploadResponse.region,
          bucket: uploadResponse.bucket,
        },
        key: uploadResponse.fileKey || '',
        onProgress: (params) => {
          updateStatus({ uploadProgress: Math.round(params.percent) });
        },
      });

      if (cosResult?.statusCode !== 200) {
        throw new Error(t('文件上传失败'));
      }

      // 更新状态为成功
      updateStatus({
        uploadStatus: 'success',
        uploadProgress: 100,
        uploadUrl: cosResult.Location,
      });
    } catch (error) {
      // 更新状态为失败
      const errorMsg = error instanceof Error ? error.message : t('上传失败');
      updateStatus({
        uploadStatus: 'error',
        errorMessage: errorMsg,
      });
    }
  };

  // 组件挂载时开始上传
  useEffect(() => {
    if (uploadStatus === 'pending') {
      uploadFile();
    }
  }, []);

  // 获取文件后缀名
  const fileExtension = file.name.split('.').pop()
    ?.toLowerCase() || 'file';
  const formattedSize = formatByteAdaptive(file.size);

  // 根据上传状态显示不同的状态信息
  const getStatusDisplay = () => {
    switch (localStatus.uploadStatus) {
      case 'uploading':
        return t('上传中 {{progress}}%', { progress: localStatus.uploadProgress || 0 });
      case 'success':
        return `${fileExtension} | ${formattedSize.num}${formattedSize.unit}`;
      case 'error':
        return t('上传失败');
      case 'pending':
      default:
        return `${fileExtension} | ${formattedSize.num}${formattedSize.unit}`;
    }
  };

  return (
    <div className={styles.fileItem}>
      <div><FileIcon /></div>
      <div className={styles.fileContent}>
        <div className={styles.fileName}>{file.name}</div>
        <div className={classNames(styles.fileSize, {
          [styles.uploading]: localStatus.uploadStatus === 'uploading',
          [styles.success]: localStatus.uploadStatus === 'success',
          [styles.error]: localStatus.uploadStatus === 'error',
        })}>
          {getStatusDisplay()}
        </div>
      </div>
      <span
        className={styles.fileDelete}
        onClick={() => onDelete(extendedFile.id)}
      >
        <AgentDelete />
      </span>
    </div>
  );
};
